import { TrainingStatus } from './enums'
import { CustomerAddress, CustomerDetails, ComplianceStatus } from '../../../types'
import { UserTraining } from '../../practice/dentalenhancements/types'
import { standardDateFormat } from '../../../helpers/date'
import { getFullNameOrEmail } from '../../../redux/user'

export type ExportCustomer = Pick<
  CustomerDetails,
  'customer_number' | 'name' | 'doctor_name' | 'street' | 'city' | 'state' | 'zip'
> & {
  customer_address?: CustomerAddress
}

export interface CorpExportCustomer extends ExportCustomer {
  users_training?: UserTraining[]
}

export interface CorpTrainingByYearRow {
  parent_customer_number: string
  customer_number: string
  customer_name: string
  training_name: string
  year: number
  training_active: boolean
  users_completed: number
  users_incomplete: number
  overdue: number
  due_soon: number
}

const getAddressFields = (customer: ExportCustomer) => {
  const street = (customer.customer_address?.street ?? customer.street ?? '').trim()
  const city = (customer.customer_address?.city ?? customer.city ?? '').trim()
  const state = (customer.customer_address?.state ?? customer.state ?? '').trim()
  const zip = (customer.customer_address?.zip ?? customer.zip ?? '').trim()

  return { street, city, state, zip }
}

export const getCustomersById = <T extends ExportCustomer>(customers: T[]) => {
  return new Map<string, T>(
    customers.map((customer) => [customer.customer_number, customer])
  )
}

export const getTrainingStatus = (code: ComplianceStatus) => {
  const statusByCompliance = new Map([
    [ComplianceStatus.Complete, TrainingStatus.Complete],
    [ComplianceStatus.InProgress, TrainingStatus.Due],
    [ComplianceStatus.NotStarted, TrainingStatus.PastDue],
  ])

  return statusByCompliance.get(code) ?? TrainingStatus.Inactive
}

export const getTrainingName = (userTraining: UserTraining) => {
  return userTraining.training?.name || 'Unknown'
}

export const getActiveTrainings = (userTrainings: UserTraining[]) => {
  return userTrainings.filter((userTraining) => {
    return userTraining.compliance_status !== ComplianceStatus.NotApplicable
      && Boolean(userTraining.user?.account_active)
  })
}

export const flattenCorpUserTrainings = (customers: CorpExportCustomer[]) => {
  return customers.flatMap((customer) =>
    (customer.users_training ?? [])
      .filter((userTraining) => userTraining.compliance_status !== ComplianceStatus.NotApplicable)
      .map((userTraining) => ({
        ...userTraining,
        customer_number: customer.customer_number,
      }))
  )
}

export const getCustomerHeader = (customer: ExportCustomer) => {
  const { street, city, state, zip } = getAddressFields(customer)

  return [
    { A: customer.name.trim() },
    { A: customer.doctor_name },
    { A: street },
    { A: `${city} ${state} ${zip}` },
    { A: `Customer #: ${customer.customer_number}` },
    { A: '' },
  ]
}

export const getAllTrainingRow = (
  userTraining: UserTraining,
  childrenById: Map<string, ExportCustomer>
) => {
  const teamMember = getFullNameOrEmail(userTraining.user)
  const customer = childrenById.get(userTraining.customer_number)
  const { street, city, state, zip } = getAddressFields(customer ?? {} as ExportCustomer)
  const year = userTraining.training?.year ? `${userTraining.training.year}` : ''
  const trainingName = getTrainingName(userTraining)
  const enrollmentDate = userTraining.enrollment_date ? standardDateFormat(userTraining.enrollment_date) : ''
  const completionDate = userTraining.completion_date ? standardDateFormat(userTraining.completion_date) : ''
  const credits = (userTraining.completion_date && userTraining.training?.ce_credits) || ''

  return {
    Status: getTrainingStatus(userTraining.compliance_status),
    'Customer Number': customer?.customer_number ?? '',
    'Practice Name': customer?.name?.trim() ?? '',
    Street: street,
    City: city,
    State: state,
    Zip: zip,
    'Team Member': teamMember,
    Year: year,
    'Course Name': trainingName,
    Enrolled: enrollmentDate,
    Completed: completionDate,
    'CE Credits Earned': credits,
  }
}

export const getTrainingRowsByName = (
  userTrainings: UserTraining[],
  childrenById: Map<string, ExportCustomer>
) => {
  const rowsByName = new Map<string, ReturnType<typeof getAllTrainingRow>[]>()

  userTrainings.forEach((userTraining) => {
    const trainingName = getTrainingName(userTraining)
    const row = getAllTrainingRow(userTraining, childrenById)

    rowsByName.get(trainingName)?.push(row)
      ?? rowsByName.set(trainingName, [row])
  })

  return rowsByName
}

export const getTrainingByPracticeRow = (userTraining: UserTraining) => {
  const teamMember = getFullNameOrEmail(userTraining.user)
  const year = userTraining.training?.year ? `${userTraining.training.year}` : ''
  const trainingName = getTrainingName(userTraining)
  const enrollmentDate = userTraining.enrollment_date ? standardDateFormat(userTraining.enrollment_date) : ''
  const completionDate = userTraining.completion_date ? standardDateFormat(userTraining.completion_date) : ''
  const credits = (userTraining.completion_date && userTraining.training?.ce_credits) || ''

  return {
    Status: getTrainingStatus(userTraining.compliance_status),
    'Team Member': teamMember,
    Year: year,
    'Course Name': trainingName,
    Enrolled: enrollmentDate,
    Completed: completionDate,
    'CE Credits Earned': credits,
  }
}

export const formatTrainingActiveStatus = (trainingActive: boolean) => (trainingActive ? 'Active' : 'Inactive')

export const formatByYearRow = (row: CorpTrainingByYearRow) => ({
  'Customer Number': row.customer_number,
  'Practice Name': row.customer_name.trim(),
  Training: row.training_name,
  Year: row.year,
  'Training Status': formatTrainingActiveStatus(row.training_active),
  'Users Completed': row.users_completed,
  'Users Incomplete': row.users_incomplete,
  Overdue: row.overdue,
})

export const sortByYearRows = (rows: CorpTrainingByYearRow[]) => {
  return [...rows].sort((a, b) => {
    if (a.training_active !== b.training_active) {
      return a.training_active ? -1 : 1
    }

    const practiceCompare = a.customer_name.trim().localeCompare(b.customer_name.trim())
    if (practiceCompare !== 0) {
      return practiceCompare
    }

    return a.training_name.localeCompare(b.training_name)
  })
}
